import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Alert, Divider, TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import subscriptionService from '../../../services/subscriptionService';
import { useNavigate } from 'react-router-dom';

const PaymentMethodModal = ({ open, onClose, pkg, currentBalance, onSuccess }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [validatingPromo, setValidatingPromo] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [promoCode, setPromoCode] = useState('');

    // Promo Info
    const [promoResult, setPromoResult] = useState(null);
    const [promoError, setPromoError] = useState('');

    const finalPrice = promoResult && promoResult.valid ? promoResult.finalPrice : Number(pkg?.price);

    // Reset on open
    useEffect(() => {
        if (open) {
            setError('');
            setPromoError('');
            setSuccessMsg('');
            setPromoCode('');
            setPromoResult(null);
            setLoading(false);
        }
    }, [open]);

    const handleBuyWallet = async () => {
        if (currentBalance < finalPrice) {
            setError('Số dư không đủ để thanh toán.');
            return;
        }

        try {
            setLoading(true);
            await subscriptionService.buyPackage(pkg.id, promoCode);
            setSuccessMsg('Đăng ký thành công!');
            setTimeout(() => {
                onSuccess();
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.message || 'Có lỗi xảy ra.');
            setLoading(false);
        }
    };

    const handleDeposit = () => {
        onClose();
        navigate('/deposit');
    };

    const handleApplyPromo = async () => {
        if (!promoCode.trim()) {
            setPromoError('Vui lòng nhập mã khuyến mãi');
            return;
        }
        try {
            setValidatingPromo(true);
            setPromoError('');
            setPromoResult(null);
            const res = await subscriptionService.validatePromo(pkg.id, promoCode.trim());
            if (res.success && res.data.valid) {
                setPromoResult(res.data);
            }
        } catch (err) {
            setPromoError(err.response?.data?.message || 'Có lỗi xảy ra khi kiểm tra mã');
            setPromoResult(null);
        } finally {
            setValidatingPromo(false);
        }
    };

    const renderContent = () => {
        if (successMsg) {
            return (
                <Box textAlign="center" py={4}>
                    <CheckCircleIcon sx={{ fontSize: 60, color: '#4ade80', mb: 2 }} />
                    <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ color: 'white' }}>Đăng Ký Thành Công</Typography>
                    <Typography sx={{ color: '#cbd5e1' }}>{successMsg}</Typography>
                </Box>
            );
        }

        return (
            <Box py={1}>
                <Typography variant="h6" gutterBottom align="center" sx={{ color: '#cbd5e1', mb: 3 }}>Xác nhận thanh toán</Typography>

                <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', p: 3, borderRadius: 2, mb: 3, border: '1px solid rgba(255,255,255,0.1)' }}>
                    <Box display="flex" justifyContent="space-between" mb={1.5}>
                        <Typography sx={{ color: '#94a3b8' }}>Gói dịch vụ:</Typography>
                        <Typography fontWeight="bold" sx={{ color: 'white' }}>{pkg?.name}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1.5}>
                        <Typography sx={{ color: '#94a3b8' }}>Giá tiền:</Typography>
                        <Box textAlign="right">
                            {promoResult && promoResult.valid ? (
                                <>
                                    <Typography sx={{ color: '#94a3b8', textDecoration: 'line-through', fontSize: '0.9rem' }}>
                                        {Number(pkg?.price).toLocaleString()} VND
                                    </Typography>
                                    <Typography fontWeight="bold" sx={{ color: '#4ade80', fontSize: '1.2rem' }}>
                                        {finalPrice.toLocaleString()} VND
                                    </Typography>
                                </>
                            ) : (
                                <Typography fontWeight="bold" sx={{ color: '#06b6d4', fontSize: '1.2rem' }}>
                                    {Number(pkg?.price).toLocaleString()} VND
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)', borderStyle: 'dashed' }} />
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center">
                            <AccountBalanceWalletIcon sx={{ mr: 1, color: '#94a3b8', fontSize: 20 }} />
                            <Typography sx={{ color: '#94a3b8' }}>Số dư hiện tại:</Typography>
                        </Box>
                        <Typography
                            fontWeight="bold"
                            sx={{ color: currentBalance >= finalPrice ? '#4ade80' : '#f87171' }}
                        >
                            {currentBalance.toLocaleString()} VND
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 3 }}>
                        <Box display="flex" gap={1}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Mã khuyến mãi (nếu có)"
                                variant="outlined"
                                value={promoCode}
                                onChange={(e) => {
                                    setPromoCode(e.target.value.toUpperCase());
                                    // Reset promo result if user changes code
                                    if (promoResult || promoError) {
                                        setPromoResult(null);
                                        setPromoError('');
                                    }
                                }}
                                error={!!promoError}
                                helperText={promoError || (promoResult ? `Đã áp dụng mã: Giảm ${promoResult.discountAmount.toLocaleString()} VND` : false)}
                                FormHelperTextProps={{
                                    sx: { color: promoResult ? '#4ade80' : '#f87171', ml: 0 }
                                }}
                                sx={{
                                    input: { color: 'white', textTransform: 'uppercase' },
                                    label: { color: '#94a3b8' },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: promoError ? '#f87171' : (promoResult ? '#4ade80' : 'rgba(255,255,255,0.2)') },
                                        '&:hover fieldset': { borderColor: promoError ? '#f87171' : (promoResult ? '#4ade80' : '#3b82f6') },
                                        '&.Mui-focused fieldset': { borderColor: promoError ? '#f87171' : (promoResult ? '#4ade80' : '#3b82f6') }
                                    }
                                }}
                            />
                            <Button
                                variant="outlined"
                                onClick={handleApplyPromo}
                                disabled={validatingPromo || !promoCode.trim()}
                                sx={{
                                    minWidth: '90px',
                                    height: '40px',
                                    color: '#38bdf8',
                                    borderColor: 'rgba(56,189,248,0.5)',
                                    '&:hover': { borderColor: '#38bdf8', bgcolor: 'rgba(56,189,248,0.1)' }
                                }}
                            >
                                {validatingPromo ? '...' : 'Áp Dụng'}
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {currentBalance < finalPrice ? (
                    <Alert severity="warning" sx={{ mb: 2, bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#fbbf24', '& .MuiAlert-icon': { color: '#fbbf24' } }}>
                        Số dư ví không đủ. Vui lòng nạp thêm <strong>{(finalPrice - currentBalance).toLocaleString()} VND</strong> để tiếp tục.
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(6, 182, 212, 0.1)', color: '#22d3ee', '& .MuiAlert-icon': { color: '#22d3ee' } }}>
                        Số tiền sẽ được trừ trực tiếp vào ví của bạn.
                    </Alert>
                )}
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: '#0f172a', /* Slate 900 */
                    color: 'white',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.1)'
                }
            }}
        >
            <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 2 }}>
                Thanh Toán Dịch Vụ
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
                {renderContent()}
                {error && <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#f87171', '& .MuiAlert-icon': { color: '#f87171' } }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ p: 3, justifyContent: 'center', pt: 0 }}>
                {!successMsg && (
                    <>
                        <Button
                            onClick={onClose}
                            disabled={loading}
                            sx={{ mr: 1, color: '#94a3b8', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.05)' } }}
                        >
                            Đóng
                        </Button>

                        {currentBalance >= finalPrice ? (
                            <Button
                                variant="contained"
                                onClick={handleBuyWallet}
                                disabled={loading}
                                size="large"
                                sx={{
                                    minWidth: 150,
                                    bgcolor: '#3b82f6',
                                    fontWeight: 'bold',
                                    '&:hover': { bgcolor: '#2563eb' }
                                }}
                            >
                                {loading ? 'Đang xử lý...' : 'Thanh Toán Ngay'}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleDeposit}
                                size="large"
                                sx={{
                                    minWidth: 150,
                                    bgcolor: '#3b82f6',
                                    fontWeight: 'bold',
                                    '&:hover': { bgcolor: '#2563eb' }
                                }}
                            >
                                Nạp Tiền Ngay
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PaymentMethodModal;
